import type { TextDirection } from '@/generated/core-enums'
/**
 * How a language names itself, and which way it reads.
 *
 * One module because several surfaces ask: the clinical Translate menu offers
 * languages, its language picker switches between the saved ones, each field
 * companion captions the text it shows, and a localized field lists the
 * languages a value carries. A second copy of `Intl.DisplayNames` would be a
 * second answer to "what is this language called", and the two would disagree
 * the first time one of them grew a special case — so the naming itself is
 * `intl.ts`'s, and what is decided here is which tag is asked about and in
 * which language the answer is wanted.
 */
import { platformName } from '@/shared/i18n/intl'

const RTL_TAGS = new Set(['he', 'ar', 'fa', 'ur'])

/** The language's own name, in itself. A clinician who landed in the wrong
 *  interface must still recognise the one that returns them. */
export function endonymOf(tag: string): string {
  return platformName(tag, tag, 'language')
}

/** The language's name in the interface currently being read. */
export function languageDisplay(tag: string, locale: string): string {
  return platformName(tag, locale, 'language')
}

/**
 * THE SHORTEST THING THAT STILL NAMES A LANGUAGE: `he` for `he-IL`, `en` for
 * `en-GB`, `sr` for `sr-Latn`.
 *
 * Here rather than in the one component that draws it, for the reason the
 * module exists: which part of a tag a surface asks about is this module's
 * decision, and the same `split('-')[0]` written beside a view is how one
 * surface comes to abbreviate `zh-Hant` and the next one not to.
 *
 * A PRIVATE-USE OR GRANDFATHERED TAG IS ANSWERED WHOLE. `x-clinic` shortens to
 * the letter `x`, which names nothing, and `i-klingon` to `i` — so a primary
 * subtag too short to be a language leaves the tag as it stands.
 */
export function primaryLanguageSubtag(tag: string): string {
  const primary = tag.split('-')[0] ?? tag
  return primary.length < 2 ? tag : primary
}

/**
 * THE LANGUAGE AND ITS TAG IN ONE STRING, for the slots that must name a
 * language where the field's two-element caption cannot stand: an accessible
 * name, a hint, a notice parameter.
 *
 * The tag is there because the compact mark a reader SEES is the primary
 * subtag, and the pair is what tells `sr` from `sr-Latn` and `zh` from
 * `zh-Hant` — the one case where the abbreviation drops the part that
 * distinguishes. The localized field's open rows pair the same two facts as
 * separate elements of their caption; this is that pair spelled for a slot
 * that holds only text.
 *
 * A LANGUAGE THE PLATFORM CANNOT NAME is answered by its tag alone, because
 * `platformName` falls back to the code and `en (en)` says one thing twice.
 */
export function languageEvidence(tag: string, locale: string): string {
  const name = languageDisplay(tag, locale)
  return name === tag ? tag : `${name} (${tag})`
}

/** The direction of the LANGUAGE, for a label that carries no text yet. Text
 *  that exists takes `dir="auto"` instead, which reads the value itself. */
export function languageDirection(tag: string): TextDirection {
  return RTL_TAGS.has(primaryLanguageSubtag(tag)) ? 'rtl' : 'ltr'
}
